import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Tab, useMediaQuery, useTheme } from '@mui/material';
import { Screen } from '../../components';
import { makeStyles } from '@mui/styles';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { UserContext } from '../../lib/contexts';
import { NutrientsContextProvider } from '../../lib/contexts/NutrientsContext';
import AssessDietButton from '../../components/AssessDietButton';
import PatientNutrientProfileBox from '../PatientsScreen/components/PatientNutrientProfileBox';
import BioBox from '../../components/BioBox';
import DietBox from '../../components/DietBox';
import useDiet from '../../lib/hooks/useDiet';
import useDietCompare from '../../lib/hooks/useDietCompare';
import { getWorkflowToken } from '../../lib/helpers/helpers';
import usePatientNutrients from '../../lib/hooks/usePatientNutrients';
import WorkflowBox from '../../components/WorkflowBox';

const useStyles = makeStyles((theme) => ({
  screen: {
    width: '96%',
    margin: '10px auto auto',
  },
  tabPanel: {
    margin: 0,
    padding: 0,
    width: '100%',
  },
  tabRoot: {
    color: theme.palette.admin.dark,
    borderColor: theme.palette.admin.dark,
  },
  TabTextColor: {
    color: theme.palette.admin.main,
    borderColor: theme.palette.admin.main,
  },
}));

export const DietQualityScreen = () => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    state: { user, userObject },
  } = useContext(UserContext);

  // we only have 2 tabs
  const [currentTab, setCurrentTab] = useState('0');

  const userId = user?.id;

  // get workflow token so we can get the nutrient data
  const workflowToken = getWorkflowToken(userObject?.workflowShareUrl);
  const { isLoading: nutrientsDataLoading, data: nutrientsData } = usePatientNutrients(workflowToken);

  const assessmentData = userObject?.lastWorkflow;

  const { isLoading: currentDietLoading, data: currentDiet } = useDiet(
    userObject?.lastType,
    userObject?.lastQuality,
    userId,
  );

  const { isLoading: goalDietLoading, data: goalDiet } = useDiet(userObject?.goalType, userObject?.goalQuality, userId);

  const { isLoading: goalDietCompareLoading, data: goalDietCompare } = useDietCompare(
    userObject?.lastType,
    userObject?.lastQuality,
    userObject?.goalType,
    userObject?.goalQuality,
    userId,
  );

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const a11yProps = (index) => {
    return {
      value: String(index),
      id: `scrollable-tab-${index}`,
      'aria-controls': `scrollable-tabpanel-${index}`,
    };
  };

  return (
    <Screen
      title={'Diet Quality - Diet ID'}
      showSidebarNav={true}
      isAdmin={false}
    >
      <Grid
        container
        alignItems="center"
        spacing={0}
        className={classes.screen}
      >
        <Box sx={{ width: '100%', padding: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              marginBottom: { xs: '24px', sm: '32px' },
              gap: { xs: '24px', sm: 0 },
              padding: 0,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Your Diet Quality
            </Typography>
            <AssessDietButton />
          </Box>

          <TabContext value={currentTab}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                marginBottom: { xs: '24px', sm: '32px' },
                padding: 0,
              }}
            >
              <TabList
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="secondary"
                textColor="primary"
                aria-label="diet quality tabs"
                sx={{
                  width: { xs: 'auto', sm: '100%' },
                  maxWidth: '100%',
                  padding: 0,
                  '& .MuiTab-root': {
                    color: 'rgba(0, 0, 0, 0.60)',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '24px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    width: '100px',
                    minWidth: '100px',
                    padding: '12px 16px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  },
                  '& .MuiTab-root:not(:last-child)': {
                    marginRight: '16px',
                  },
                  '& .Mui-selected': {
                    color: `${theme.palette.admin.main} !important`,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: `${theme.palette.admin.main}`,
                  },
                }}
              >
                <Tab
                  label="Results"
                  {...a11yProps(0)}
                />
                <Tab
                  label="Nutrients"
                  {...a11yProps(1)}
                />
              </TabList>
            </Box>

            <TabPanel
              value="0"
              className={classes.tabPanel}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  width: '100%',
                  padding: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: '24px', sm: 0 },
                    width: '100%',
                    padding: 0,
                    justifyContent: 'space-between',
                    position: 'relative',
                    flexWrap: { sm: 'wrap', lg: 'nowrap' },
                    alignItems: 'flex-start',
                  }}
                >
                  {/* BioBox column */}
                  {!currentDietLoading &&
                    !goalDietLoading &&
                    currentDiet?.data?.name &&
                    currentDiet?.data?.quality &&
                    goalDiet?.data?.name &&
                    goalDiet?.data?.quality && (
                      <Box
                        sx={{
                          width: { xs: '100%', sm: '360px' },
                          flexShrink: 0,
                          flexGrow: 0,
                          marginRight: { xs: 0, sm: '24px' },
                          boxSizing: 'border-box',
                        }}
                      >
                        <BioBox
                          bioData={{
                            dietName: currentDiet?.data?.name,
                            dietQuality: currentDiet?.data?.quality,
                            idealName: goalDiet?.data?.name,
                            idealQuality: goalDiet?.data?.quality,
                            ...userObject,
                          }}
                          assessmentData={assessmentData}
                          checkinHistoryData={userObject?.lastCheckinAt}
                        />
                      </Box>
                    )}

                  {/* DietBoxes column - with fixed width to match the nutrients tab */}
                  <Box
                    sx={{
                      width: { xs: '100%', sm: '100%', lg: 'calc(100% - 384px)' },
                      flexShrink: 1,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '24px',
                      marginTop: { sm: '24px', lg: 0 },
                      boxSizing: 'border-box',
                    }}
                  >
                    {userObject?.lastType && userObject?.lastQuality && (
                      <DietBox
                        title="Your Current Diet"
                        type={userObject?.lastType}
                        quality={userObject?.lastQuality}
                      />
                    )}

                    {userObject?.goalType && userObject?.goalQuality && (
                      <DietBox
                        title="Your Goal Diet"
                        type={userObject?.goalType}
                        quality={userObject?.goalQuality}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel
              value="1"
              className={classes.tabPanel}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: '24px',
                  width: '100%',
                  padding: 0,
                }}
              >
                {workflowToken && !nutrientsDataLoading && nutrientsData && (
                  <NutrientsContextProvider workflowToken={workflowToken}>
                    <Box sx={{ width: { xs: '100%', sm: '336px' }, flexShrink: 0 }}>
                      <PatientNutrientProfileBox data={nutrientsData} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        flex: 1,
                      }}
                    >
                      <WorkflowBox type="macronutrients" />
                      <WorkflowBox type="nutrients" />
                      <WorkflowBox type="fats" />
                      <WorkflowBox type="minerals" />
                      <WorkflowBox type="vitamins" />
                    </Box>
                  </NutrientsContextProvider>
                )}
              </Box>
            </TabPanel>
          </TabContext>
        </Box>
      </Grid>
    </Screen>
  );
};

export default DietQualityScreen;
